import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, FileText, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { logger } from "@/lib/logger";

// Section structure for visual hierarchy. The actual text comes from the
// backend (/api/terms) so we can update wording centrally; this just decides
// what's a heading vs a paragraph based on the canonical formatting.
function splitSections(text) {
  const paragraphs = text.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  return paragraphs;
}

export default function TermsAcceptanceModal() {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [terms, setTerms] = useState({ text: "", version: "" });
  const [signedName, setSignedName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!user || user === false) return;
    if (user.active === false) return;       // deactivated → apology only
    if (user.terms_accepted) return;          // already signed
    (async () => {
      try {
        const { data } = await api.get("/terms");
        setTerms(data);
        setOpen(true);
      } catch (e) {
        // If the /terms call fails, don't block the user — they'll be prompted next login.
        logger.warn("terms fetch failed; will retry next login", e);
      }
    })();
  }, [user]);

  // Track scroll-to-bottom so we only enable Agree once they've actually
  // scrolled through the document. Avoids the "click without reading" pattern.
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const reachedBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 20;
    if (reachedBottom && !hasScrolled) setHasScrolled(true);
  };

  const submit = async () => {
    if (!agreed || !signedName.trim()) return;
    setBusy(true);
    try {
      await api.post("/auth/accept-terms", { signed_name: signedName.trim(), version: terms.version });
      // Refresh user so downstream modals (KYC) take over
      const { data: me } = await api.get("/auth/me");
      setUser(me);
      toast.success("Thank you — your acknowledgement has been recorded");
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record acknowledgement");
    } finally {
      setBusy(false);
    }
  };

  if (!user || user === false || user.terms_accepted) return null;

  const sections = splitSections(terms.text || "");
  const canSubmit = hasScrolled && agreed && signedName.trim().length >= 2 && !busy;

  return (
    <Dialog open={open} onOpenChange={() => { /* required acknowledgement */ }}>
      <DialogContent
        className="max-w-2xl p-0 overflow-hidden border-0 max-h-[90vh] grid grid-rows-[auto_1fr_auto]"
        data-testid="terms-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        hideClose
      >
        {/* Header */}
        <header className="bg-zinc-950 text-white px-8 py-6">
          <div className="flex items-center gap-3 text-zinc-500 text-[11px] uppercase tracking-widest">
            <FileText className="w-3.5 h-3.5" />
            <span>Required acknowledgement · One-time</span>
          </div>
          <h2 className="font-display text-3xl font-black tracking-tighter mt-2">Terms &amp; Conditions</h2>
          <p className="text-zinc-400 text-sm mt-2">
            Please read carefully before you continue. After signing, you&apos;ll be asked to upload your KYC documents.
          </p>
        </header>

        {/* Scrollable body */}
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="overflow-y-auto px-8 py-6 bg-white"
          data-testid="terms-body"
        >
          {!terms.text ? (
            <div className="text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading terms…</div>
          ) : (
            <article className="prose prose-sm prose-zinc max-w-none">
              {sections.map((p, i) => {
                const sectionKey = `s-${i}-${p.slice(0, 24)}`;
                // Detect the warning-process bullet block and render it as a list
                if (p.startsWith("•") || p.includes("First Warning")) {
                  const lines = p.split("\n").map((l) => l.trim()).filter(Boolean);
                  return (
                    <ul key={sectionKey} className="space-y-1.5 my-3 ml-0 list-none">
                      {lines.map((l) => {
                        const m = l.match(/^[•\-*]?\s*(.+?)\s*[—-]\s*(.+)$/);
                        const lineKey = `l-${l.slice(0, 40)}`;
                        if (m) {
                          return (
                            <li key={lineKey} className="flex items-start gap-3 text-zinc-800">
                              <span className="font-semibold tabular-nums whitespace-nowrap">{m[1]}</span>
                              <span className="text-zinc-300">—</span>
                              <span>{m[2]}</span>
                            </li>
                          );
                        }
                        return <li key={lineKey} className="text-zinc-800">{l}</li>;
                      })}
                    </ul>
                  );
                }
                return (
                  <p key={sectionKey} className="text-zinc-800 leading-relaxed text-[14.5px] mb-3 whitespace-pre-wrap">
                    {p}
                  </p>
                );
              })}
              <div className="mt-6 pt-4 border-t border-zinc-200 text-[11px] uppercase tracking-widest text-zinc-400">
                Version {terms.version} · Happy Retreats
              </div>
            </article>
          )}
        </div>

        {/* Scroll cue (visible until they reach the bottom) */}
        {!hasScrolled && (
          <div className="absolute left-1/2 -translate-x-1/2 bottom-[150px] flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500 bg-white/95 px-3 py-1.5 rounded-full shadow border border-zinc-200 pointer-events-none">
            <ChevronDown className="w-3 h-3 animate-bounce" /> Scroll to read fully
          </div>
        )}

        {/* Footer — checkbox + sign + button */}
        <footer className="bg-zinc-50 border-t border-zinc-200 px-8 py-5 space-y-3">
          <label className="flex items-start gap-3 cursor-pointer" data-testid="terms-agree-label">
            <Checkbox
              checked={agreed}
              onCheckedChange={(v) => setAgreed(Boolean(v))}
              disabled={!hasScrolled}
              className="mt-0.5"
              data-testid="terms-agree-checkbox"
            />
            <span className={`text-sm ${hasScrolled ? "text-zinc-900" : "text-zinc-400"}`}>
              I have read and agree to the Terms &amp; Conditions above.
            </span>
          </label>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <Input
                value={signedName}
                onChange={(e) => setSignedName(e.target.value)}
                placeholder={`Type your full name to sign (e.g. ${user?.name || "Your Name"})`}
                disabled={!agreed}
                className="h-11"
                data-testid="terms-signature"
              />
            </div>
            <Button
              onClick={submit}
              disabled={!canSubmit}
              className="bg-zinc-950 hover:bg-zinc-800 h-11 px-6"
              data-testid="terms-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "I acknowledge"}
            </Button>
          </div>
          <p className="text-[11px] text-zinc-500">
            Your acknowledgement is recorded with a timestamp and the version of these terms.
          </p>
        </footer>
      </DialogContent>
    </Dialog>
  );
}
